import debug from "debug";
import { mount, unmount } from "svelte";
import ImportDesignModal from "../../lib/components/modals/ImportDesingModal/ImportDesignModal.svelte";
import ViewDesignsModal from "../../lib/components/modals/ViewDesignsModal/ViewDesignsModal.svelte";
import AdminLoginModal from "../../lib/components/modals/AdminLoginModal/AdminLoginModal.svelte";
import { createEventTrigger } from "../../lib/events/eventTrigger";
import { MyAccountManager } from "./MyAccountManager";
import { CartHandler } from "./CartHandler";

const log = debug("app:svelteUIController");

interface MountedComponent {
  instance: ReturnType<typeof mount>;
  target: HTMLElement;
}

interface EventData {
  event: string;
  [key: string]: unknown;
}

/**
 * Controller for managing Svelte UI components based on EventHub events.
 * Listens to events and mounts/unmounts Svelte components accordingly.
 * @category Components
 * @subcategory SvelteUI
 */
export class SvelteUIController {
  private experience: any;
  private eventHub: any;
  private mountedComponents: Map<string, MountedComponent>;
  private myAccountManager: MyAccountManager;
  private cartHandler: CartHandler;

  /**
   * Creates an instance of SvelteUIController.
   * @param experience - The experience object containing eventHub and other context.
   */
  constructor(experience: any) {
    this.experience = experience;
    this.eventHub = experience.eventHub;
    this.mountedComponents = new Map<string, MountedComponent>();

    // Instantiate MyAccountManager to handle account-related events
    this.myAccountManager = new MyAccountManager(experience);

    // Instantiate CartHandler to handle cart-related events
    this.cartHandler = new CartHandler(experience);

    this.setupEventListeners();
    log("SvelteUIController initialized");
  }

  /**
   * Sets up event listeners for mounting Svelte components.
   * Note: Cart-related events (addToCart, viewCart, sendRFQ) are handled by CartHandler.
   */
  private setupEventListeners(): void {
    // Listen for importDesign event to mount ImportDesignModal
    this.eventHub.on("importDesign", (data: EventData) => {
      log("Received importDesign event, mounting ImportDesignModal", data);
      this.mountImportDesignModal(data);
    });

    // Listen for openViewDesignsModal event to mount ViewDesignsModal
    this.eventHub.on("openViewDesignsModal", () => {
      log("Received openViewDesignsModal event, mounting ViewDesignsModal");
      this.mountViewDesignsModal();
    });

    // Listen for showAdminLoginModal event
    this.eventHub.on("showAdminLoginModal", () => {
      log("Received showAdminLoginModal event, mounting AdminLoginModal");
      this.mountAdminLoginModal();
    });
  }

  /**
   * Mounts the ImportDesignModal component.
   * @param data - Data to pass to the component.
   */
  private mountImportDesignModal(data: EventData): void {
    // Unmount existing modal if present
    if (this.mountedComponents.has("importDesignModal")) {
      this.unmountComponent("importDesignModal");
    }

    // Mount directly to body - Dialog.Portal will render its content to body anyway
    const container = document.body;

    // Create trigger function for the modal
    const trigger = createEventTrigger(this.experience);

    // Mount the Svelte component using Svelte 5 API
    // The component uses bind:open internally, so we pass open: true to show it initially
    const instance = mount(ImportDesignModal, {
      target: container,
      props: {
        open: true,
        trigger: trigger,
      },
    });

    this.mountedComponents.set("importDesignModal", {
      instance,
      target: container,
    });

    log("ImportDesignModal mounted");
  }

  /**
   * Mounts the ViewDesignsModal component.
   */
  private mountViewDesignsModal(): void {
    // Unmount existing modal if present
    if (this.mountedComponents.has("viewDesignsModal")) {
      this.unmountComponent("viewDesignsModal");
    }

    // Mount directly to body - Dialog.Portal will render its content to body anyway
    const container = document.body;

    // Mount the Svelte component using Svelte 5 API
    const instance = mount(ViewDesignsModal, {
      target: container,
      props: {
        open: true,
        experience: this.experience,
      },
    });

    this.mountedComponents.set("viewDesignsModal", {
      instance,
      target: container,
    });

    log("ViewDesignsModal mounted");
  }

  /**
   * Mounts the AdminLoginModal component.
   * Shown when a ?source=admin view-design URL is opened by a non-authenticated user.
   */
  private mountAdminLoginModal(): void {
    if (this.mountedComponents.has("adminLoginModal")) {
      this.unmountComponent("adminLoginModal");
    }

    const container = document.body;

    const instance = mount(AdminLoginModal, {
      target: container,
      props: {
        open: true,
        onLogin: () => {
          this.eventHub.emit("signInClick");
        },
      },
    });

    this.mountedComponents.set("adminLoginModal", {
      instance,
      target: container,
    });

    log("AdminLoginModal mounted");
  }

  /**
   * Creates a container element for mounting Svelte components.
   * Dialog.Portal handles rendering to body, so this container should be minimal.
   * @param className - CSS class name for the container.
   * @returns The created container element.
   */
  private createContainer(className: string): HTMLElement {
    const container = document.createElement("div");
    container.className = className;
    // Minimal container that won't affect layout - Dialog.Portal will render elsewhere
    container.style.cssText = "position: absolute; width: 0; height: 0; overflow: visible; pointer-events: none;";
    document.body.appendChild(container);
    return container;
  }

  /**
   * Unmounts a Svelte component by its ID.
   * @param id - The ID of the component to unmount.
   */
  private unmountComponent(id: string): void {
    const mounted = this.mountedComponents.get(id);
    if (mounted) {
      unmount(mounted.instance);
      // Don't try to remove body, only remove if it's a custom container
      if (mounted.target && mounted.target !== document.body && mounted.target.parentNode) {
        mounted.target.remove();
      }
      this.mountedComponents.delete(id);
      log(`Component ${id} unmounted`);
    }
  }

  /**
   * Cleans up all mounted components and removes event listeners.
   */
  cleanup(): void {
    log("Cleaning up SvelteUIController");

    // Cleanup MyAccountManager
    this.myAccountManager.cleanup();

    // Cleanup CartHandler
    this.cartHandler.cleanup();

    // Unmount all components
    this.mountedComponents.forEach((mounted, id) => {
      this.unmountComponent(id);
    });

    // Remove event listeners
    this.eventHub.off("importDesign");
    this.eventHub.off("openViewDesignsModal");
    this.eventHub.off("showAdminLoginModal");

    log("SvelteUIController cleanup complete");
  }
}

